import React, { Suspense, useEffect, useMemo, useState } from "react";
import { ProductFruits } from "react-product-fruits";

import { App as AntApp, ConfigProvider } from "antd";
import csCZ from "antd/locale/cs_CZ";
import deDE from "antd/locale/de_DE";
import enGB from "antd/locale/en_GB";
import enUS from "antd/locale/en_US";
import plPL from "antd/locale/pl_PL";
import skSK from "antd/locale/sk_SK";
import dayjs from "dayjs";
import "dayjs/locale/cs";
import "dayjs/locale/de";
import "dayjs/locale/en-gb";
import "dayjs/locale/pl";
import "dayjs/locale/sk";

import {
  GeneralContextConsumer,
  GeneralContextProvider,
  NavContext,
  NavContextProvider,
  UserContextConsumer,
  UserContextProvider,
  acceptAllCookies,
  checkCookieConsent,
  getLanguageFromLocalStorage,
  initAnalytics,
  initCookieBanner,
  toggleCookiesPreferencesButtonVisibility,
  useAppType,
  useGetFarms,
  useLanguage,
  useLocation,
  useNetworkState,
} from "utils";

import { LOCALE_DICT } from "consts";

import { ErrorBoundary, Layout } from "components";

import { AnalyticsUpdater } from "pages/app/_app/components/AnalyticsUpdater";
import { AppNavBottom } from "pages/app/_app/components/AppNavBottom";
import { AppNavSide } from "pages/app/_app/components/AppNavSide";
import { HeaderMobile } from "pages/app/_app/components/HeaderMobile";
import { HeaderDesktop } from "pages/app/_app/components/header-desktop/HeaderDesktop";
import { TermsOverlay } from "pages/app/_app/components/terms-overlay/TermsOverlay";
import { AppRoutes } from "pages/app/_app/routes";
import { Offline } from "pages/app/account/Offline";
import { CropMixComposerContextProvider } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";

const SHOW_COOKIE_PREFERENCES_BUTTON_PATHS = ["/sign-in", "sign-up"];

const { Content, Header } = Layout;

export const App = () => {
  const appType = useAppType();
  const network = useNetworkState();
  const location = useLocation();
  const { data: farms } = useGetFarms();

  useLanguage();

  const [cookiesAccepted, setCookiesAccepted] = useState(checkCookieConsent());
  const [initializedAnalyticsWithConsent, setInitializedAnalyticsWithConsent] = useState(false);
  const [programTermsConsentMissing, setProgramTermsConsentMissing] = useState(false);

  const language = getLanguageFromLocalStorage({ forDates: true });

  const { antdLocale } = useMemo(() => {
    const localeMap = {
      de: { antd: deDE, dayjs: "de" },
      pl: { antd: plPL, dayjs: "pl" },
      "cs-cz": { antd: csCZ, dayjs: "cs" },
      "sk-sk": { antd: skSK, dayjs: "sk" },
      "en-us": { antd: enUS, dayjs: "en" },
      "en-gb": { antd: enGB, dayjs: "en-gb" },
    };

    const currentLocale = LOCALE_DICT[language] || LOCALE_DICT.de;
    const selectedLocale = localeMap[currentLocale] || localeMap.de;

    dayjs.locale(selectedLocale.dayjs);

    return {
      antdLocale: selectedLocale.antd,
    };
  }, [language]);

  useEffect(() => {
    initCookieBanner();
    initAnalytics();
  }, []);

  useEffect(() => {
    if (farms) {
      setProgramTermsConsentMissing(farms.some((farm) => farm.carbonProgram && !farm.hasAcceptedLatestProgramTerms));
    }
  }, [farms]);

  useEffect(() => {
    if (SHOW_COOKIE_PREFERENCES_BUTTON_PATHS.some((path) => location.pathname.includes(path))) {
      toggleCookiesPreferencesButtonVisibility(true);
      return;
    }

    toggleCookiesPreferencesButtonVisibility(false);

    if (!cookiesAccepted) {
      acceptAllCookies();
      setCookiesAccepted(true);
      return;
    }

    // re-initialize analytics after the user consent
    if (!initializedAnalyticsWithConsent) {
      initAnalytics();
      setInitializedAnalyticsWithConsent(true);
    }
  }, [cookiesAccepted, initializedAnalyticsWithConsent, location]);

  // layout when user is offline
  if (!network) {
    return (
      <ConfigProvider theme={{ hashed: false }} locale={antdLocale}>
        <AntApp>
          <Layout>
            <Content>
              <ErrorBoundary>
                <Offline />
              </ErrorBoundary>
            </Content>
          </Layout>
        </AntApp>
      </ConfigProvider>
    );
  }

  // layout when user is on desktop
  if (appType.desktop) {
    return (
      <ConfigProvider theme={{ hashed: false }} locale={antdLocale}>
        <AntApp>
          <Layout className="app-layout">
            <Suspense fallback={<></>}>
              <GeneralContextProvider>
                <GeneralContextConsumer>
                  {({ appNavSideCollapsed }) => (
                    <UserContextProvider>
                      <UserContextConsumer>
                        {({ termsOverlayHidden, user }) =>
                          user && (!user?.hasAcceptedLatestPlatformTerms || programTermsConsentMissing) && !termsOverlayHidden ? (
                            <TermsOverlay />
                          ) : (
                            <CropMixComposerContextProvider>
                              <NavContextProvider>
                                <ErrorBoundary>
                                  <AppNavSide />
                                </ErrorBoundary>
                                <Layout>
                                  <NavContext.Consumer>
                                    {({ showNavbar }) => (
                                      <>
                                        {user && (
                                          <Header className={appNavSideCollapsed || !appType?.largeDesktop ? "" : "menu-open"}>
                                            <AnalyticsUpdater /> {/* analytics updater - only render when user is logged in */}
                                            <ErrorBoundary>
                                              <HeaderDesktop />
                                            </ErrorBoundary>
                                          </Header>
                                        )}
                                        <Content className={user && showNavbar ? "with-navbar" : ""}>
                                          <ProductFruits
                                            workspaceCode={process.env.REACT_APP_PRODUCT_FRUITS_KEY ?? ""}
                                            language={language.substring(0, 2)}
                                            user={{
                                              username: user?.id,
                                              firstname: user?.firstName,
                                              role: user?.farm?.accountingMethodology,
                                            }}
                                          />
                                          <ErrorBoundary>
                                            <AppRoutes />
                                          </ErrorBoundary>
                                        </Content>
                                      </>
                                    )}
                                  </NavContext.Consumer>
                                </Layout>
                              </NavContextProvider>
                            </CropMixComposerContextProvider>
                          )
                        }
                      </UserContextConsumer>
                    </UserContextProvider>
                  )}
                </GeneralContextConsumer>
              </GeneralContextProvider>
            </Suspense>
          </Layout>
        </AntApp>
      </ConfigProvider>
    );
  }

  // layout if user is on mobile
  return (
    <ConfigProvider theme={{ hashed: false }} locale={antdLocale}>
      <AntApp>
        <Layout>
          <Suspense fallback={<></>}>
            <GeneralContextProvider>
              <UserContextProvider>
                <UserContextConsumer>
                  {({ termsOverlayHidden, user }) =>
                    user && (!user?.hasAcceptedLatestPlatformTerms || programTermsConsentMissing) && !termsOverlayHidden ? (
                      <TermsOverlay />
                    ) : (
                      <NavContextProvider>
                        <ErrorBoundary>
                          <HeaderMobile />
                        </ErrorBoundary>
                        <Content>
                          <ErrorBoundary>
                            <AppRoutes />
                          </ErrorBoundary>
                        </Content>
                        <ErrorBoundary>
                          <AppNavBottom />
                        </ErrorBoundary>
                      </NavContextProvider>
                    )
                  }
                </UserContextConsumer>
              </UserContextProvider>
            </GeneralContextProvider>
          </Suspense>
        </Layout>
      </AntApp>
    </ConfigProvider>
  );
};
